import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, ArrowRight, Instagram, 
  LayoutDashboard, LogOut, Settings, User, 
  ChevronDown
} from 'lucide-react';
import { Logo } from './Logo';
import { useStore } from '../store/useStore';


export const MarketingLayout: React.FC = () => {
  const { isAuthenticated, currentUser } = useStore();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const consent = localStorage.getItem('wholescale-cookie-consent');
    if (!consent) {
      setShowCookieConsent(true);
    }
  }, []);

  const getInitials = () => {
    if (!currentUser) return '';
    if (currentUser.avatar && currentUser.avatar.length <= 2) return currentUser.avatar;
    return currentUser.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const navLinks = [
    { name: 'Blog', href: '/blog' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Home', href: '/' },
    { name: 'Compare', href: '/compare' },
    { name: 'About', href: '/about' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-black text-[#dee5ff] selection:bg-indigo-500/30 overflow-visible">
      {/* Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-200 animate-astral-nav ${
          isScrolled ? 'py-3 bg-black/80 backdrop-blur-2xl border-b border-indigo-500/10 shadow-2xl' : 'py-6 bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between overflow-visible">
          <Link to="/" className="flex items-center gap-3 group">
            <Logo size={40} showText={true} />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.href;
              return (
                <Link 
                  key={link.name} 
                  to={link.href}
                  className={`relative py-2 text-sm font-black uppercase tracking-widest transition-colors hover:text-indigo-400 ${
                    isActive ? 'text-indigo-400' : 'text-[#a3aac4]'
                  }`}
                >
                  {link.name}
                  {isActive && (
                    <motion.div 
                      layoutId="nav-underline"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 rounded-full"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <div className="flex items-center gap-6">
                <Link 
                  to={currentUser?.role === 'buyer' || currentUser?.role === 'seller' ? "/client-portal" : "/dashboard"} 
                  className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  <LayoutDashboard size={18} />
                  {currentUser?.role === 'buyer' || currentUser?.role === 'seller' ? 'Go to My Portal' : 'Go to CRM'}
                </Link>
                <div className="relative">
                  <button 
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-sm font-black shadow-lg shadow-indigo-600/20 group-hover:scale-105 transition-transform border border-indigo-400/20 italic">
                      {getInitials()}
                    </div>
                    <ChevronDown size={14} className={`text-[#6d758c] transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {userMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-[2001]" onClick={() => setUserMenuOpen(false)} />
                      <div className="absolute right-0 mt-3 w-64 rounded-2xl bg-black/95 backdrop-blur-3xl border border-indigo-500/20 shadow-2xl p-2 z-50 ">
                        <div className="p-4 border-b border-white/5 mb-2">
                          <p className="text-[10px] font-black text-[#6d758c] uppercase tracking-[0.2em] mb-1">Signed in as</p>
                          <p className="text-sm font-black truncate text-white italic">{currentUser?.name}</p>
                        </div>
                        <Link 
                          to="/settings" 
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 text-xs font-black uppercase tracking-widest transition-colors text-[#a3aac4] hover:text-indigo-400"
                        >
                          <User size={16} /> Profile
                        </Link>
                        <Link 
                          to="/settings" 
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 text-xs font-black uppercase tracking-widest transition-colors text-[#a3aac4] hover:text-indigo-400"
                        >
                          <Settings size={16} /> Settings
                        </Link>
                        <div className="h-px bg-white/5 my-2" />
                        <button 
                          onClick={async () => {
                            await useStore.getState().logout();
                            setUserMenuOpen(false);
                            navigate('/login');
                          }}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-red-500/10 text-xs font-black uppercase tracking-widest transition-colors text-red-400"
                        >
                          <LogOut size={16} /> Sign Out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                <Link to="/login" className="text-xs font-black uppercase tracking-[0.2em] text-[#a3aac4] hover:text-white transition-colors">
                  Log in
                </Link>
                <Link 
                  to="/signup" 
                  className="px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-xs font-black uppercase tracking-[0.2em] transition-all hover:shadow-[0_10px_30px_rgba(99,102,241,0.3)] hover:scale-105 active:scale-95 flex items-center gap-2 hover-glow hover-lift"
                >
                  Sign Up <ArrowRight size={16} />
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden p-2 text-gray-300 hover:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence mode="wait">
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0, y: -20 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -20 }}
              transition={{ duration: 0.3, ease: "circOut" }}
              className="md:hidden absolute top-full left-0 right-0 bg-black border-b border-indigo-500/10 p-6 space-y-4 overflow-hidden shadow-2xl z-[90]"
            >
              <div className="flex flex-col gap-4">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link 
                      to={link.href}
                      className={`block text-lg font-black uppercase tracking-widest transition-colors ${
                        location.pathname === link.href ? 'text-indigo-400' : 'text-gray-400 hover:text-white'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
                
                <div className="h-px bg-white/5 my-2" />
                
                {isAuthenticated ? (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-3 p-3 bg-white/5 rounded-2xl border border-white/5">
                      <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center font-black italic shadow-lg shadow-indigo-600/20">
                        {getInitials()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-black truncate text-white uppercase italic">{currentUser?.name}</p>
                        <p className="text-[10px] text-gray-500 truncate uppercase tracking-[0.2em]">{currentUser?.email}</p>
                      </div>
                    </div>
                    <Link 
                      to={currentUser?.role === 'buyer' || currentUser?.role === 'seller' ? "/client-portal" : "/dashboard"} 
                      className="flex items-center gap-3 text-lg font-black uppercase tracking-widest text-indigo-400 hover:text-indigo-300 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <LayoutDashboard size={20} /> {currentUser?.role === 'buyer' || currentUser?.role === 'seller' ? 'Go to My Portal' : 'Go to CRM'}
                    </Link>
                    <button 
                      onClick={async () => {
                        await useStore.getState().logout();
                        setMobileMenuOpen(false);
                        navigate('/login');
                      }}
                      className="w-full flex items-center gap-3 text-lg font-black uppercase tracking-widest text-red-500 hover:text-red-400 transition-colors"
                    >
                      <LogOut size={20} /> Sign Out
                    </button>
                  </motion.div>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="flex flex-col gap-4"
                  >
                    <Link 
                      to="/login" 
                      className="block py-4 text-center text-lg font-black uppercase tracking-widest text-gray-400 hover:text-white transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Log in
                    </Link>
                    <Link 
                      to="/signup" 
                      className="block w-full py-4 rounded-xl bg-indigo-600 text-center font-black uppercase tracking-widest text-white shadow-lg shadow-indigo-600/20 hover:bg-indigo-500 transition-all hover:scale-[1.02]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign Up
                    </Link>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-24 overflow-visible w-full">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-black border-t border-indigo-500/10 py-32 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-12 md:gap-4">
          <div className="col-span-2 md:col-span-1 pr-0 md:pr-12">
            <div className="flex items-center gap-3 mb-8">
              <Logo size={40} showText={true} />
            </div>
            <p className="text-[#6d758c] text-lg leading-relaxed mb-10 font-medium">
              The all-in-one operating system for modern real estate professionals. Automate, scale, and build your empire.
            </p>
            <div className="flex gap-6">
              <a href="https://x.com/wholescaleos" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all hover:scale-110">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a href="https://www.instagram.com/wholescaleos/" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all hover:scale-110"><Instagram size={24} /></a>
            </div>
          </div>
          
          <div className="md:pl-12">
            <h4 className="font-black mb-8 text-sm uppercase tracking-[0.2em] text-white">Platform</h4>
            <ul className="space-y-4 text-sm font-black uppercase tracking-widest text-[#a3aac4]">
              <li><Link to="/pricing" className="hover:text-indigo-400 transition-colors">Pricing</Link></li>
              <li><Link to="/integrations" className="hover:text-indigo-400 transition-colors">Integrations</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-black mb-8 text-sm uppercase tracking-[0.2em] text-white">Resources</h4>
            <ul className="space-y-4 text-sm font-black uppercase tracking-widest text-[#a3aac4]">
              <li><Link to="/about" className="hover:text-indigo-400 transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="hover:text-indigo-400 transition-colors">Contact</Link></li>
              <li><Link to="/privacy" className="hover:text-indigo-400 transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-indigo-400 transition-colors">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-20 pt-8 border-t border-white/5 text-center text-gray-500 text-xs">
          © {new Date().getFullYear()} WholeScale OS. All rights reserved.
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      {showCookieConsent && (
        <div className="fixed bottom-8 left-8 right-8 md:left-auto md:right-8 md:w-[420px] z-50 p-8 rounded-[2.5rem] bg-black/95 border border-indigo-500/20 shadow-2xl animate-in slide-in-from-bottom-10 lg:slide-in-from-right-10 duration-200 backdrop-blur-3xl hover-lift">
           <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 shrink-0 shadow-inner">
                 <Settings size={28} className="animate-spin-slow" />
              </div>
              <div className="space-y-5">
                 <div>
                    <h4 className="font-black text-white uppercase italic tracking-tight">Sovereign Data Controls</h4>
                    <p className="text-[11px] text-[#6d758c] leading-relaxed mt-2 font-medium">
                       We use high-performance cookies to ensure the integrity of your session and provide the best OS experience.
                    </p>
                 </div>
                 <div className="flex items-center gap-4">
                    <button 
                      onClick={() => {
                        localStorage.setItem('wholescale-cookie-consent', 'true');
                        setShowCookieConsent(false);
                      }}
                      className="px-8 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] font-black uppercase tracking-[0.2em] transition-all shadow-lg shadow-indigo-600/20 hover-glow"
                    >
                      Accept Protocol
                    </button>
                    <Link to="/privacy" className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] hover:text-white transition-colors">Learn More</Link>
                 </div>
              </div>
           </div>
        </div>
      )}

      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 12s linear infinite;
        }
      `}</style>
    </div>
  );
};

